import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import LayoutContainer from 'components/Common/LayoutContainer'
import TextButton from 'LuxKit/Button/TextButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { removeOfferComparisonSelection, clearAllOfferComparisonSelections } from 'actions/OfferComparisonActions'
import { selectOfferComparisonEnabled, selectOfferComparisonSelections } from 'selectors/offerComparisonSelectors'
import zIndex from 'styles/tools/z-index'
import OfferComparisonModal from './OfferComparisonModal'
import OfferFooterChip from './OfferFooterChip'
import { MAX_COMPARISON_SELECTIONS } from 'constants/offer'

const Root = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  padding-top: ${rem(20)};
  padding-bottom: ${rem(20)};
  box-shadow: ${props => props.theme.shadow.top.medium};
  z-index: ${zIndex.stickyFooter};
`

const ChipsSection = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`

const AddAnother = styled(AssistChip)`
  &.variant-filled:disabled {
    outline: 1px dashed ${props => props.theme.palette.neutral.default.three};
    outline-offset: -1px;
    color: ${props => props.theme.palette.neutral.default.three};
    box-shadow: none;
    cursor: default;
  }
`

function OfferSelectionStickyFooter() {
  const dispatch = useAppDispatch()
  const enabled = useAppSelector(selectOfferComparisonEnabled)
  const selections = useAppSelector(selectOfferComparisonSelections)
  const canAddSelection = selections.length < MAX_COMPARISON_SELECTIONS

  const showModal = useContext(ModalContext)

  const deleteSelection = useCallback((selectionId: string) => {
    dispatch(removeOfferComparisonSelection(selectionId))
  }, [dispatch])

  const clearAll = useCallback(() => {
    dispatch(clearAllOfferComparisonSelections())
  }, [dispatch])

  const openComparisonModal = useCallback(() => {
    showModal(<OfferComparisonModal />)
  }, [showModal])

  if (!enabled || selections.length === 0) {
    return null
  }

  return <Root>
    <LayoutContainer size="xl">
      <Group direction="vertical" gap={16} desktopDirection="horizontal" desktopHorizontalAlign="space-between">
        <ChipsSection>
          <HorizontalContainerWithFadedOverflow gap={8}>
            {selections.map(selection => (
              <OfferFooterChip key={selection.selectionId} selection={selection} onRemove={deleteSelection} />
            ))}

            {canAddSelection && <AddAnother
              variant="filled"
              size="medium"
              disabled
            >
              <BodyText variant="medium">
                {selections.length === 1 && <>Select another offer</>}
                {selections.length > 1 && <>Select one more?</>}
              </BodyText>
            </AddAnother>}
          </HorizontalContainerWithFadedOverflow>
        </ChipsSection>

        <Group direction="horizontal" horizontalAlign="space-between" gap={16}>
          <TextButton kind="tertiary" onClick={clearAll}>Clear all</TextButton>
          <TextButton kind="primary" onClick={openComparisonModal}>
            {selections.length < 3 ? 'Compare offers' : `Compare ${selections.length} offers`}
          </TextButton>
        </Group>
      </Group>
    </LayoutContainer>
  </Root>
}

export default OfferSelectionStickyFooter
